import React, { useRef, useState, useEffect } from "react";
import styled from "styled-components";

const Container = styled.div`
  margin-top: 10px;
`;

const Title = styled.div`
  font-weight: bold;
  margin-bottom: 8px;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const ArrowButton = styled.button`
  background: #FFF0C3;
  border: 1px solid #c49c00;
  width: 40px;
  height: 40px;
  font-size: 22px;
  font-weight: bold;
  border-radius: 999px;
  cursor: pointer;
  user-select: none;

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const Viewport = styled.div`
  overflow: hidden;
  flex: 1; /* middle takes full space */
`;

const TasksWrapper = styled.div`
  display: flex;
  gap: 4px;
  transition: transform 0.3s ease;
`;

const TaskBox = styled.div`
  background: #d6e2f3;
  border: 1px solid #7892b5;
  padding: 8px 12px;
  width: 150px;
  text-align: center;
  font-size: 13px;
  font-weight: 600;
  border-radius: 2px;
  flex-shrink: 0;
`;

interface TaskItem {
  label: string;
  count: number;
}

export default function MyTasksBar({ tasks }: { tasks: TaskItem[] }) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const viewportRef = useRef<HTMLDivElement>(null);

  const [offset, setOffset] = useState(0);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);

  const STEP = 124; // tile width + gap

  const updateButtons = () => {
    if (!viewportRef.current || !wrapperRef.current) return;

    const viewportWidth = viewportRef.current.offsetWidth;
    const contentWidth = wrapperRef.current.scrollWidth;

    setCanLeft(offset > 0);
    setCanRight(offset + viewportWidth < contentWidth);
  };

  useEffect(() => {
    updateButtons();
    window.addEventListener("resize", updateButtons);
    return () => window.removeEventListener("resize", updateButtons);
  }, [offset, tasks]);

  const moveRight = () => {
    if (!viewportRef.current || !wrapperRef.current) return;
    const viewportWidth = viewportRef.current.offsetWidth;
    const contentWidth = wrapperRef.current.scrollWidth;

    const newOffset = Math.min(offset + STEP, contentWidth - viewportWidth);
    setOffset(newOffset);
  };

  const moveLeft = () => {
    const newOffset = Math.max(offset - STEP, 0);
    setOffset(newOffset);
  };

  return (
    <Container>

      <Row>
        {/* LEFT ARROW */}
        <ArrowButton disabled={!canLeft} onClick={moveLeft}>
          ‹
        </ArrowButton>

        {/* TILES AREA */}
        <Viewport ref={viewportRef}>
          <TasksWrapper
            ref={wrapperRef}
            style={{ transform: `translateX(-${offset}px)` }}
          >
            {tasks.map((t, i) => (
              <TaskBox key={i}>
                <div>{t.label}</div>
                <div>{t.count}</div>
              </TaskBox>
            ))}
          </TasksWrapper>
        </Viewport>

        {/* RIGHT ARROW */}
        <ArrowButton disabled={!canRight} onClick={moveRight}>
          ›
        </ArrowButton>
      </Row>
    </Container>
  );
}
